import { useRef, useEffect, useState, useCallback } from "react";
import { Image as ImageIcon, Refresh, Check, Clear } from "@mui/icons-material";
import { CircularProgress } from "@mui/material";
import SignatureCanvas from "react-signature-canvas";
import { getAttachmentUrlString, isValidAttachmentKey } from "@utils/attachment.utils";

interface SignatureUploadProps {
	signature?: string;
	uploadedSignatureKey?: string | null;
	isUploading: boolean;
	isEditMode: boolean;
	onSignatureChange: (dataUrl: string | null) => void;
	t: (key: string) => string;
	pendingSignatureDataUrl?: string | null;
}

const SignatureUpload: React.FC<SignatureUploadProps> = ({
	signature,
	uploadedSignatureKey,
	isUploading,
	isEditMode,
	onSignatureChange,
	t,
	pendingSignatureDataUrl,
}) => {
	const sigCanvasRef = useRef<SignatureCanvas>(null);
	const uploadTimeoutRef = useRef<NodeJS.Timeout | null>(null);

	// State for the actual signature URL
	const [signatureUrl, setSignatureUrl] = useState<string>("");
	const [isLoadingSignature, setIsLoadingSignature] = useState<boolean>(false);
	const [hasDrawn, setHasDrawn] = useState<boolean>(false);

	// Check if there's a newly uploaded signature (not yet saved)
	const hasNewSignature = !!uploadedSignatureKey;
	const hasPendingSignature = !!pendingSignatureDataUrl;

	// Fetch signature URL when signature key changes
	useEffect(() => {
		const fetchSignatureUrl = async () => {
			if (signature && isValidAttachmentKey(signature)) {
				setIsLoadingSignature(true);
				try {
					const url = await getAttachmentUrlString(signature);
					if (url) {
						setSignatureUrl(url);
					}
				} catch (error) {
					console.error("Failed to fetch signature URL:", error);
				} finally {
					setIsLoadingSignature(false);
				}
			} else {
				setSignatureUrl("");
			}
		};

		fetchSignatureUrl();
	}, [signature]);

	// Clear canvas and reset state when edit mode changes
	useEffect(() => {
		if (!isEditMode) {
			setHasDrawn(false);
			// Clear any pending upload timeout
			if (uploadTimeoutRef.current) {
				clearTimeout(uploadTimeoutRef.current);
				uploadTimeoutRef.current = null;
			}
		}
	}, [isEditMode]);

	// Cleanup timeout on unmount
	useEffect(() => {
		return () => {
			if (uploadTimeoutRef.current) {
				clearTimeout(uploadTimeoutRef.current);
			}
		};
	}, []);

	// Handle canvas end drawing - store signature locally (will be uploaded on Save)
	const handleEnd = useCallback(() => {
		if (sigCanvasRef.current && !sigCanvasRef.current.isEmpty()) {
			setHasDrawn(true);
			
			// Clear any existing timeout
			if (uploadTimeoutRef.current) {
				clearTimeout(uploadTimeoutRef.current);
			}
			
			// Store signature locally after a short delay (will be uploaded when Save is clicked)
			uploadTimeoutRef.current = setTimeout(() => {
				if (sigCanvasRef.current && !sigCanvasRef.current.isEmpty() && !hasNewSignature) {
					const dataUrl = sigCanvasRef.current.toDataURL("image/png");
					onSignatureChange(dataUrl); // This just stores locally, doesn't upload
				}
				uploadTimeoutRef.current = null;
			}, 500); // 500ms delay after drawing ends
		}
	}, [onSignatureChange, hasNewSignature]);

	// Clear signature canvas
	const handleClear = useCallback(() => {
		// Clear any pending upload timeout
		if (uploadTimeoutRef.current) {
			clearTimeout(uploadTimeoutRef.current);
			uploadTimeoutRef.current = null;
		}
		
		if (sigCanvasRef.current) {
			sigCanvasRef.current.clear();
			setHasDrawn(false);
			onSignatureChange(null);
		}
	}, [onSignatureChange]);


	// If not in edit mode, just show the signature preview or placeholder
	if (!isEditMode) {
		return (
			<div className="mt-6 border-t pt-6">
				<h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
					<ImageIcon className="w-4 h-4" />
					{t("psdaCommon.profile.signature.title") || "Signature"}
				</h4>
				{isLoadingSignature ? (
					<div className="w-full h-32 border border-gray-200 rounded-lg flex items-center justify-center bg-white">
						<CircularProgress size={24} />
					</div>
				) : signatureUrl ? (
					<div className="w-full h-32 border border-gray-200 rounded-lg flex items-center justify-center bg-white p-2">
						<img src={signatureUrl} alt="Signature" className="max-w-full max-h-full object-contain" />
					</div>
				) : (
					<div className="w-full h-32 border border-gray-200 rounded-lg flex items-center justify-center bg-white text-gray-400">
						<p className="text-sm">{t("psdaCommon.profile.signature.noSignature") || "No signature uploaded"}</p>
					</div>
				)}
			</div>
		);
	}

	return (
		<div className="mt-6 border-t pt-6">
			<h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
				<ImageIcon className="w-4 h-4" />
				{t("psdaCommon.profile.signature.title") || "Signature"}
				<span className="text-red-500">*</span>
			</h4>

			{/* Instructions */}
			<div className="mb-3 p-2 bg-blue-50 rounded-lg border border-blue-100">
				<p className="text-xs text-blue-700">
					<strong>{t("psdaCommon.profile.signature.instructions") || "Instructions"}:</strong>{" "}
					{t("psdaCommon.profile.signature.drawInstructions") ||
						"Draw your signature in the box below using mouse or touch"}
				</p>
			</div>

			{/* Signature Canvas Container */}
			<div
				className={`relative w-full border-2 rounded-lg overflow-hidden transition-colors ${
					hasNewSignature
						? "border-green-400 bg-green-50"
						: hasPendingSignature || hasDrawn
						? "border-blue-400 bg-white"
						: "border-gray-300 bg-white"
				}`}>
				{hasNewSignature ? (
					// Show success state for saved signature
					<div className="flex flex-col items-center justify-center h-40 text-green-600">
						<Check className="w-10 h-10 mb-2" />
						<p className="text-sm font-medium">{t("psdaCommon.profile.signature.uploaded") || "Signature saved"}</p>
						<p className="text-xs text-green-500 mt-1">
							{t("psdaCommon.profile.signature.clickSave") || "Click Save to update profile"}
						</p>
						<button
							type="button"
							onClick={handleClear}
							className="mt-3 text-xs text-green-600 hover:text-green-800 underline flex items-center gap-1">
							<Refresh className="w-3 h-3" />
							{t("psdaCommon.profile.signature.redraw") || "Draw Again"}
						</button>
					</div>
				) : (
					<>
						{/* Canvas */}
						<SignatureCanvas
							ref={sigCanvasRef}
							penColor="#1a1a1a"
							canvasProps={{
								className: "w-full h-40 cursor-crosshair",
								style: { width: "100%", height: "160px", backgroundColor: "transparent" },
							}}
							onEnd={handleEnd}
						/>

						{/* Placeholder text when canvas is empty */}
						{!hasDrawn && (
							<div className="absolute inset-0 flex items-center justify-center pointer-events-none">
								<p className="text-gray-300 text-sm select-none">
									{t("psdaCommon.profile.signature.placeholder") || "Sign here"}
								</p>
							</div>
						)}
					</>
				)}

				{isUploading && (
					<div className="absolute inset-0 bg-black bg-opacity-50 rounded-lg flex items-center justify-center">
						<CircularProgress size={28} className="text-white" />
					</div>
				)}
			</div>

			{/* Action Buttons - only show Clear button when canvas has content and not yet saved */}
			{!hasNewSignature && (hasDrawn || hasPendingSignature) && (
				<div className="flex items-center justify-end gap-2 mt-3">
					<button
						type="button"
						onClick={handleClear}
						disabled={isUploading}
						className={`flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md transition-colors ${
							!isUploading
								? "text-red-600 hover:bg-red-50 border border-red-200"
								: "text-gray-300 border border-gray-200 cursor-not-allowed"
						}`}>
						<Clear className="w-4 h-4" />
						{t("psdaCommon.profile.signature.clear") || "Clear"}
					</button>
				</div>
			)}

			<p className="text-xs text-gray-500 mt-2">
				{t("psdaCommon.profile.signature.helperText") ||
					"Please draw your signature clearly. This will be used for official documents."}
			</p>
		</div>
	);
};

export default SignatureUpload;
